import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { staffingPlanAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Sparkles, Loader2, Users, IndianRupee,
  AlertCircle, CheckCircle, Clock, TrendingUp, Info, FileText
} from 'lucide-react';

const PRIORITY_CONFIG = {
  CRITICAL: { color: 'bg-red-100 text-red-700 border-red-200',    dot: 'bg-red-500'    },
  HIGH:     { color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  MEDIUM:   { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  LOW:      { color: 'bg-gray-100 text-gray-600 border-gray-200',  dot: 'bg-gray-400'   },
};

function formatINR(amount) {
  if (!amount) return null;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0
  }).format(amount);
}

export default function ViewStaffingPlan() {
  const { id } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [availability, setAvailability] = useState(null);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    try {
      const res = await staffingPlanAPI.get(id);
      const data = res.data.data;
      setPlan(data);

      // Parse roles JSON
      if (data.recommendedRoles) {
        try { setRoles(JSON.parse(data.recommendedRoles)); } catch { setRoles([]); }
      }
      // Parse availability snapshot
      if (data.staffAvailabilitySnapshot) {
        try { setAvailability(JSON.parse(data.staffAvailabilitySnapshot)); } catch {}
      }
    } catch {
      toast.error('Failed to load staffing plan');
      navigate('/company/staffing-plans');
    } finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  if (!plan) return null;

  const isFailed = plan.status === 'FAILED';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/staffing-plans')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* Plan header */}
        <div className="bg-gradient-to-br from-slate-900 to-teal-900 rounded-2xl p-7 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 80% 30%, rgba(20,184,166,0.25) 0%, transparent 60%)' }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-teal-400" />
              <span className="text-teal-400 text-xs font-bold uppercase tracking-widest">AI Staffing Plan</span>
            </div>
            <h1 className="text-xl font-bold text-white mb-1">{plan.planName}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mt-2">
              <span>{plan.facilityName}</span>
              {plan.facilityCity && <span>· {plan.facilityCity}</span>}
              {plan.generatedAt && (
                <span>· Generated {new Date(plan.generatedAt).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric'
                })}</span>
              )}
            </div>
          </div>
        </div>

        {/* Failed state */}
        {isFailed && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-red-800">Plan generation failed</p>
              <p className="text-red-700 text-sm mt-1">{plan.errorMessage || 'An error occurred while generating this plan.'}</p>
              <button onClick={() => navigate(`/company/staffing-plans/generate?facilityId=${plan.facilityId}`)}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-red-700 transition">
                Try Again
              </button>
            </div>
          </div>
        )}

        {!isFailed && (
          <>
            {/* Summary */}
            {plan.planSummary && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                    <FileText className="h-4 w-4 text-teal-600" />
                  </div>
                  <h2 className="font-bold text-slate-800">Plan Summary</h2>
                </div>
                <p className="text-gray-700 leading-relaxed">{plan.planSummary}</p>
              </div>
            )}

            {/* Key numbers */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center">
                <Users className="h-7 w-7 text-teal-600 mx-auto mb-2" />
                <p className="text-3xl font-black text-slate-800">{plan.totalStaffNeeded ?? '—'}</p>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mt-1">Total Staff Needed</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center">
                <IndianRupee className="h-7 w-7 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-black text-slate-800">
                  {plan.estimatedMonthlyCostMin
                    ? `${formatINR(plan.estimatedMonthlyCostMin)}`
                    : '—'}
                </p>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mt-1">Est. Monthly Min</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center">
                <TrendingUp className="h-7 w-7 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-black text-slate-800">
                  {plan.estimatedMonthlyCostMax
                    ? `${formatINR(plan.estimatedMonthlyCostMax)}`
                    : '—'}
                </p>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mt-1">Est. Monthly Max</p>
              </div>
            </div>

            {/* Recommended roles */}
            {roles.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <h2 className="font-bold text-slate-800">Recommended Roles</h2>
                </div>

                <div className="space-y-4">
                  {roles.map((role, i) => {
                    const pCfg = PRIORITY_CONFIG[role.priority] || PRIORITY_CONFIG.MEDIUM;
                    return (
                      <div key={i}
                        className="border border-gray-100 rounded-xl p-5">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1 ${pCfg.dot}`} />
                            <div>
                              <h3 className="font-bold text-slate-800">{role.role_name}</h3>
                              {role.shift_distribution && (
                                <p className="text-xs text-gray-500 mt-0.5">{role.shift_distribution}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${pCfg.color}`}>
                              {role.priority}
                            </span>
                            <div className="text-right">
                              <p className="text-2xl font-black text-slate-800">{role.quantity}</p>
                              <p className="text-xs text-gray-400">staff</p>
                            </div>
                          </div>
                        </div>

                        {role.justification && (
                          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2.5 mb-3">
                            {role.justification}
                          </p>
                        )}

                        {(role.estimated_daily_rate_min || role.estimated_daily_rate_max) && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <IndianRupee className="h-3 w-3" />
                            Daily rate estimate: {' '}
                            {role.estimated_daily_rate_min && formatINR(role.estimated_daily_rate_min)}
                            {role.estimated_daily_rate_min && role.estimated_daily_rate_max && ' – '}
                            {role.estimated_daily_rate_max && formatINR(role.estimated_daily_rate_max)}
                            {' '}per person
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Staff availability in city */}
            {availability && (
              <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-teal-800 text-sm">
                      ShramSena Staff Availability in {availability.city || 'your city'}
                    </p>
                    <div className="flex gap-6 mt-2 text-sm text-teal-700">
                      <span>
                        <span className="font-black text-teal-900 text-lg">
                          {availability.verified_staff_in_city ?? 0}
                        </span>{' '}
                        verified staff in city
                      </span>
                      <span>
                        <span className="font-black text-teal-900 text-lg">
                          {availability.total_verified_staff_platform ?? 0}
                        </span>{' '}
                        on platform total
                      </span>
                    </div>
                    <p className="text-xs text-teal-600 mt-2">
                      To request staff from this plan, use the Hire Requests section.
                      Admin will match you with the best candidates.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center justify-between gap-4">
              <div>
                <p className="font-bold text-slate-800">Ready to hire?</p>
                <p className="text-sm text-gray-400 mt-0.5">
                  Browse our verified staff pool and send hire requests to get started.
                </p>
              </div>
              <button onClick={() => navigate('/company/search')}
                className="flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-teal-700 transition shadow-lg shadow-teal-500/20 shrink-0">
                Search Staff
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
